import { Component, computed, effect, ElementRef, input, model, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";

import { TypeManagerDecorator } from "../../main/type.map.service";
import { DataIngestionService } from "../data-ingestion/data.ingestion.service";
import { INGESTION_POINT_TYPE_ID } from "../data-ingestion/data.ingestion.const";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { WorkflowService } from "./service/workflow.service";
import { WorkflowActionRulesComponent } from "./workflow.action.rules.component";
import { ExpandTrigger, WorkflowSteps } from "./workflow.process.types";
import { S25Util } from "../../util/s25-util";
import { WorkflowUtil } from "./workflow.util";

@TypeManagerDecorator("workflow-action-rules-step")
@Component({
    selector: "workflow-action-rules-step",
    template: `
        <div class="action-rules-step-container">
            <div class="step-header">
                <h3>Action Rules</h3>
                <p class="step-description">
                    Configure when each action runs based on form field values. Set form context on the action above,
                    then use the dropdown to add rules. Actions that already have rules are listed below.
                </p>
            </div>

            @if (rulePresenceLoading()) {
                <p class="loading-hint">Loading action rules…</p>
            }

            @if (!workflowProcess().workflowProcessId) {
                <div class="rules-unavailable">
                    <p>Action rules will be available after saving the workflow process.</p>
                </div>
            } @else if (!eligibleActionsForRules().length) {
                <div class="no-actions">
                    <p>No actions defined. Please add and save actions in the previous step.</p>
                </div>
            } @else {
                <div class="add-rule-section">
                    <label for="add-action-select">Select an action to add a rule</label>
                    <p class="action-picker-hint">Each option is listed as Action Name - Stage Name - Form Name.</p>
                    <select
                        id="add-action-select"
                        class="action-picker"
                        [ngModel]="addActionSelectValue()"
                        (ngModelChange)="onAddActionSelectChange($event)"
                    >
                        <option value="">Choose an action…</option>
                        @for (action of eligibleActionsForRules(); track action.workflowActionId) {
                            <option [value]="action.workflowActionId">
                                {{ getActionRuleDropdownLabel(action) }}
                            </option>
                        }
                    </select>
                    @if (selectedActionForAdd()) {
                        <div class="add-action-editor">
                            <div class="add-action-editor-toolbar">
                                <button
                                    type="button"
                                    class="aw-button aw-button--danger--outline"
                                    (click)="cancelAddAction()"
                                >
                                    Cancel
                                </button>
                            </div>
                            <workflow-action-rules
                                [selectedAction]="selectedActionForAdd()"
                                [workflowProcessId]="workflowProcess().workflowProcessId"
                            ></workflow-action-rules>
                        </div>
                    }
                </div>

                <div class="actions-list">
                    @for (action of actionsWithRules(); track action.workflowActionId) {
                        <div class="action-item" [attr.data-action-id]="action.workflowActionId">
                            <div class="action-header" (click)="toggleActionExpansion(action)">
                                <div class="action-info">
                                    <span class="action-name">{{ getActionDisplayName(action) }}</span>
                                    <span class="action-context">{{ getActionRuleDropdownLabel(action) }}</span>
                                    <span class="action-type">{{ getActionTypeDisplay(action) }}</span>
                                </div>
                                <div class="action-actions">
                                    <span class="expand-icon" [class.expanded]="isActionExpanded(action)">
                                        {{ isActionExpanded(action) ? "▼" : "▶" }}
                                    </span>
                                </div>
                            </div>

                            @if (isActionExpanded(action)) {
                                <div class="action-content">
                                    <workflow-action-rules
                                        [selectedAction]="action"
                                        [workflowProcessId]="workflowProcess().workflowProcessId"
                                    ></workflow-action-rules>
                                </div>
                            }
                        </div>
                    }
                </div>

                @if (!rulePresenceLoading() && !actionsWithRules().length && !selectedActionForAdd()) {
                    <div class="no-actions">
                        <p>No action rules configured yet. Choose an action above to add a rule.</p>
                    </div>
                }
            }
        </div>
    `,
    styles: `
        .action-rules-step-container {
            padding: 20px;
        }

        .step-header {
            margin-bottom: 24px;
        }

        .step-header h3 {
            margin: 0 0 8px 0;
            color: var(--color--text-strong);
        }

        .step-description {
            margin: 0;
            color: var(--color--text-muted);
            font-size: 14px;
        }

        .loading-hint {
            color: var(--color--text-muted);
            margin-bottom: 16px;
        }

        .add-rule-section {
            margin-bottom: 24px;
            padding: 16px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
        }

        .add-rule-section label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .action-picker-hint {
            margin: 0 0 8px 0;
            color: var(--color--text-muted);
            font-size: 13px;
        }

        .add-action-editor {
            margin-top: 16px;
        }

        .add-action-editor-toolbar {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 12px;
        }

        .actions-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .action-item {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }

        .action-header {
            padding: 16px;
            background: var(--color--surface-2-bg);
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background 0.2s;
        }

        .action-header:hover {
            background: var(--color--surface-2-bg-hover);
        }

        .action-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .action-name {
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .action-context {
            font-size: 12px;
            color: var(--color--text-muted);
            font-style: italic;
        }

        .action-type {
            font-size: 11px;
            color: var(--color--text-muted);
            background: var(--color--surface-2-bg-hover);
            padding: 2px 6px;
            border-radius: 8px;
            text-transform: uppercase;
        }

        .expand-icon {
            color: var(--color--text-muted);
            font-size: 12px;
            transition: transform 0.2s;
        }

        .expand-icon.expanded {
            transform: rotate(90deg);
        }

        .action-content {
            padding: 20px;
            border-top: 1px solid var(--color--border);
        }

        .rules-unavailable {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
            background: var(--color--surface-2-bg);
            border-radius: 6px;
        }

        .action-picker {
            padding: 8px 12px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            background: var(--color--surface-bg);
            min-width: 360px;
        }

        .no-actions {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [WorkflowActionRulesComponent, FormsModule],
})
export class WorkflowActionRulesStepComponent {
    workflowProcess = input.required<WorkflowTypes.WorkflowProcess>();
    selectedStageTrigger = model<ExpandTrigger>();

    private expandedActions = signal<Set<number | string>>(new Set());
    actionsWithRules = signal<WorkflowTypes.WorkflowAction[]>([]);
    rulePresenceLoading = signal(false);
    selectedActionForAdd = signal<WorkflowTypes.WorkflowAction | null>(null);
    addActionSelectValue = signal<string>("");
    private formNameMap = signal<Map<number, string>>(new Map());

    readonly actionIdsWithRules = computed(() => {
        const ids = new Set<number>();
        for (const action of this.actionsWithRules()) {
            if (S25Util.isDefined(action.workflowActionId)) ids.add(action.workflowActionId!);
        }
        return ids;
    });

    readonly eligibleActionsForRules = computed((): WorkflowTypes.WorkflowAction[] => {
        const workflow = this.workflowProcess();
        if (!workflow.workflowProcessId) return [];
        const actions: WorkflowTypes.WorkflowAction[] = [];
        for (const stage of workflow.stages || []) {
            for (const action of stage.actions || []) {
                if (action.workflowActionId) actions.push(action);
            }
        }
        return actions;
    });

    private readonly referencedFormIds = computed(() => {
        const ids = new Set<number>();
        for (const action of this.eligibleActionsForRules()) {
            if (action.actionItemId && Number(action.actionItemTypeId) === INGESTION_POINT_TYPE_ID) {
                ids.add(action.actionItemId);
            }
        }
        return [...ids];
    });

    constructor(private el: ElementRef) {
        effect(() => {
            const expandTrigger = this.selectedStageTrigger();
            if (expandTrigger && expandTrigger.step === WorkflowSteps.Actions && S25Util.isDefined(expandTrigger.id)) {
                this.expandActionById(expandTrigger.id);
                this.selectedStageTrigger.set(undefined);
            }
        });
        effect(() => {
            this.workflowProcess();
            void this.refreshActionsWithRules();
        });
        effect(() => {
            const actionId = this.selectedActionForAdd()?.workflowActionId;
            if (!S25Util.isDefined(actionId)) return;
            if (this.actionIdsWithRules().has(actionId!)) {
                this.selectedActionForAdd.set(null);
                this.addActionSelectValue.set("");
            }
        });
        effect(() => {
            const formIds = this.referencedFormIds();
            if (formIds.length) void this.loadFormNames(formIds);
        });
    }

    async refreshActionsWithRules() {
        this.rulePresenceLoading.set(true);
        try {
            const actions = await WorkflowUtil.loadActionsWithWorkflowRules(this.workflowProcess());
            this.actionsWithRules.set(actions);
        } finally {
            this.rulePresenceLoading.set(false);
        }
    }

    cancelAddAction() {
        this.selectedActionForAdd.set(null);
        this.addActionSelectValue.set("");
        void this.refreshActionsWithRules();
    }

    onAddActionSelectChange(raw: string) {
        this.addActionSelectValue.set(raw);
        if (!raw) {
            this.selectedActionForAdd.set(null);
            return;
        }
        const actionId = Number(raw);
        const action = this.eligibleActionsForRules().find((candidate) => candidate.workflowActionId === actionId);
        if (!action) {
            this.selectedActionForAdd.set(null);
            return;
        }
        if (this.actionIdsWithRules().has(actionId)) {
            this.selectedActionForAdd.set(null);
            this.expandActionById(actionId);
            return;
        }
        for (const listed of this.actionsWithRules()) {
            if (this.isActionExpanded(listed)) {
                this.toggleActionExpansion(listed);
            }
        }
        this.selectedActionForAdd.set(action);
    }

    getStageForAction(action: WorkflowTypes.WorkflowAction): WorkflowTypes.WorkflowStage | null {
        return (
            this.workflowProcess().stages?.find((stage) =>
                stage.actions?.some(
                    (stageAction) =>
                        (stageAction.workflowActionId && stageAction.workflowActionId === action.workflowActionId) ||
                        (stageAction.id && stageAction.id === action.id),
                ),
            ) || null
        );
    }

    toggleActionExpansion(action: WorkflowTypes.WorkflowAction) {
        const actionKey = action.workflowActionId || action.id!;
        const expanded = this.expandedActions();

        if (expanded.has(actionKey)) {
            expanded.delete(actionKey);
        } else {
            expanded.add(actionKey);
        }

        this.expandedActions.set(new Set(expanded));
    }

    isActionExpanded(action: WorkflowTypes.WorkflowAction): boolean {
        const actionKey = action.workflowActionId || action.id!;
        return this.expandedActions().has(actionKey);
    }

    getFormNameForAction(action: WorkflowTypes.WorkflowAction): string | null {
        if (!action.actionItemId || Number(action.actionItemTypeId) !== INGESTION_POINT_TYPE_ID) {
            return null;
        }
        return this.formNameMap().get(action.actionItemId) ?? null;
    }

    getActionRuleDropdownLabel(action: WorkflowTypes.WorkflowAction): string {
        const parts = [this.getActionDisplayName(action)];
        const stageName = this.getStageForAction(action)?.stageName;
        if (stageName) {
            parts.push(stageName);
        }
        const formName = this.getFormNameLabel(action);
        if (formName) {
            parts.push(formName);
        }
        return parts.join(" - ");
    }

    private getFormNameLabel(action: WorkflowTypes.WorkflowAction): string | null {
        const formName = this.getFormNameForAction(action);
        if (formName) {
            return formName;
        }
        if (action.actionItemId && Number(action.actionItemTypeId) === INGESTION_POINT_TYPE_ID) {
            return `Form ${action.actionItemId}`;
        }
        return null;
    }

    private async loadFormNames(formIds: number[]) {
        const currentMap = this.formNameMap();
        const missing = formIds.filter((formId) => !currentMap.has(formId));
        if (!missing.length) return;

        try {
            const fetched = await DataIngestionService.getIngestionPointNames(missing);
            const newMap = new Map(currentMap);
            for (const [formId, name] of fetched) {
                newMap.set(formId, name);
            }
            this.formNameMap.set(newMap);
        } catch {
            // skip
        }
    }

    getActionDisplayName(action: WorkflowTypes.WorkflowAction): string {
        if (action.description?.trim()) {
            return action.description;
        }

        if (action.actionCode) {
            const systemDisplayName = WorkflowService.getSystemActionDisplayName(action.actionCode);
            if (systemDisplayName) {
                return systemDisplayName;
            }
            return action.actionCode
                .replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (letter) => letter.toUpperCase());
        }

        return `${action.actionType} Action`;
    }

    getActionTypeDisplay(action: WorkflowTypes.WorkflowAction): string {
        if (action.actionType === "SYSTEM_ACTION" && action.actionCode) {
            const systemDisplayName = WorkflowService.getSystemActionDisplayName(action.actionCode);
            if (systemDisplayName) {
                return systemDisplayName;
            }
            return action.actionCode
                .replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (letter) => letter.toUpperCase());
        }
        return action.actionType;
    }

    expandActionById(actionId: number) {
        const action = this.actionsWithRules().find((candidate) => candidate.workflowActionId === actionId);
        if (!action) return;

        const actionKey = action.workflowActionId ?? action.id!;
        const expanded = this.expandedActions();
        if (!expanded.has(actionKey)) {
            expanded.add(actionKey);
            this.expandedActions.set(new Set(expanded));
        }
        setTimeout(() => {
            const actionEl = this.el.nativeElement.querySelector(`[data-action-id="${actionKey}"]`);
            actionEl?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }
}
